import { useState, useEffect } from "react";
import { formatAddress } from "../../helpers/helpers";
import { getResolvedEnsAddress } from "../../helpers/utils";

type WalletEntry = {
  label: string;
  address: string;
};

type SavedWalletsProps = {
  setWalletAddress: (address: string, label?: string) => void;
  isDarkMode: boolean;
};

export function SavedWallets({ setWalletAddress, isDarkMode }: SavedWalletsProps) {
  const [walletAddressHistory, setWalletAddressHistory] = useState<WalletEntry[]>([]);
  const [penguinSrc, setPenguinSrc] = useState("/imgs/fp_penguin_idle.png");
  const [hoveredAddress, setHoveredAddress] = useState<string | null>(null);

  useEffect(() => {
    const stored = localStorage.getItem("walletAddresses");
    if (stored) {
      try {
        const parsed = JSON.parse(stored);

        const normalized: WalletEntry[] = parsed.map((entry: any) => {
          if (typeof entry === "string") {
            return { label: entry, address: entry };
          }

          return {
            label: entry.label ?? entry.address ?? "",
            address: entry.address ?? entry.label ?? "",
          };
        });

        setWalletAddressHistory(normalized);
        localStorage.setItem("walletAddresses", JSON.stringify(normalized));
      } catch (err) {
        console.error("Failed to parse wallet history from localStorage", err);
      }
    }
  }, []);

  const handleSelectAddress = async (entry: WalletEntry) => {
    let resolvedAddress = entry.address;
    let label = entry.label;

    if (entry.label.endsWith(".eth")) {
      const resolved = await getResolvedEnsAddress(entry.label);
      if (!resolved) {
        console.warn(`> Could not resolve ENS: ${entry.label}`);
        return;
      }
      resolvedAddress = resolved;
      label = entry.label; // keep ENS label in UI
    }

    // ✅ Pass both address (for logic) and ENS label (for UI)
    setWalletAddress(resolvedAddress, label);
  };

  const handleRemoveAddress = (addressToRemove: string) => {
    const updated = walletAddressHistory.filter((entry) => entry.address !== addressToRemove);
    localStorage.setItem("walletAddresses", JSON.stringify(updated));
    setWalletAddressHistory(updated);
  };

  return (
    <div className="flex h-full w-full flex-col items-center justify-center">
      <div
        className={`mt-2 flex flex-col items-center justify-center rounded-lg border-2 ${
          isDarkMode
            ? "border-darkgreen bg-darkblue text-white [box-shadow:inset_0_-2px_#37b764]"
            : "border-brown bg-white [box-shadow:inset_0_-2px_#342e2e]"
        } w-[22.5rem] p-3`}
      >
        <div className="flex flex-col items-center justify-center">
          <a href="https://frenpet.xyz/" target="_blank" rel="noreferrer">
            <img src={penguinSrc} alt="" className="h-32 w-36" />
          </a>
          <h1 className="font-bold">Fren Pet Analytica</h1>
          <div className="ml-1 flex flex-row">
            <img
              src={isDarkMode ? "/imgs/darkpaw.svg" : "/imgs/paw.svg"}
              alt=""
              className="mr-[0.7rem] mt-2 h-4 w-4"
            />
            <h2 className="mt-2 text-sm font-semibold">Your Fren Pet data</h2>
            <img
              src={isDarkMode ? "/imgs/darkpaw.svg" : "/imgs/paw.svg"}
              alt=""
              className="ml-2 mr-[0.3rem] mt-2 h-4 w-4"
            />
          </div>
        </div>

        <p
          className={`mt-2 text-center text-sm font-semibold ${
            isDarkMode ? "text-grey" : "text-darkbrown"
          }`}
        >
          Select a wallet
        </p>

        <div className="mb-2 mt-3 space-y-3">
          {walletAddressHistory.map((entry) => (
            <div
              key={entry.address}
              className={`flex items-center rounded-md border px-3 py-2 ${
                isDarkMode
                  ? "border-darkgreen [box-shadow:inset_0_-2px_#37b764] hover:bg-darkblack hover:text-darkgreen"
                  : "border-brown [box-shadow:inset_0_-2px_#342e2e] hover:bg-brown hover:text-white"
              } cursor-pointer`}
              onClick={() => handleSelectAddress(entry)}
            >
              <span className="w-32 truncate">
                {entry.label.endsWith(".eth") ? entry.label : formatAddress(entry.address)}
              </span>
              <button
                className={`rounded-md border-2 ${
                  isDarkMode
                    ? "border-darkgreen px-1 [box-shadow:inset_0_-1px_#37b764] hover:bg-darkgreen"
                    : "border-brown bg-white px-1 text-brown [box-shadow:inset_0_-1px_#342e2e] hover:bg-green hover:text-white"
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveAddress(entry.address);
                }}
                onMouseEnter={() => setHoveredAddress(entry.address)}
                onMouseLeave={() => setHoveredAddress(null)}
              >
                <img
                  src={
                    isDarkMode
                      ? hoveredAddress === entry.address
                        ? "/imgs/x.svg"
                        : "/imgs/xDark.svg"
                      : "/imgs/x.svg"
                  }
                  alt=""
                  className="h-6 w-6 px-1"
                />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
